<template>
    <div class="w-full flex flex-col p-8 text-ternary">
        <div v-if="isDraftEmail">
            <div class="flex flex-row items-center flex-wrap form-input" :class="{ invalid: !subject.isValid }">
                <div class="w-1/4">
                    <label for="subject">Subject</label>
                </div>
                <div class="w-3/4">
                    <input
                        type="text"
                        id="subject"
                        v-model.trim="subject.val"
                        placeholder=""
                        @blur="clearValidity('subject')"
                    />
                    <transition name="fade" mode="out-in">
                        <p v-if="!subject.isValid" class="h-4 text-xs text-red-600">
                            {{ subject.msg }}
                        </p>
                    </transition>
                </div>
            </div>
            <div class="mt-4 flex flex-row items-center flex-wrap form-input" :class="{ invalid: !recipientName.isValid }">
                <div class="w-1/4">
                    <label for="recipientName">Recipient's Name</label>
                </div>
                <div class="w-3/4">
                    <input
                        type="text"
                        id="recipientName"
                        v-model.trim="recipientName.val"
                        placeholder=""
                        @blur="clearValidity('recipientName')"
                    />
                    <transition name="fade" mode="out-in">
                        <p v-if="!recipientName.isValid" class="h-4 text-xs text-red-600">
                            {{ recipientName.msg }}
                        </p>
                    </transition>
                </div>
            </div>
            <div class="mt-4 flex flex-row items-start flex-wrap form-input" :class="{ invalid: !content.isValid }">
                <div class="w-1/4">
                    <label for="content">Content</label>
                </div>
                <div class="w-3/4">
                    <div class="h-32 p-4 rounded-xl border" :class="{ invalid_: !content.isValid }">
                        <textarea v-model="content.val" class="resize-none w-full h-full outline-none text-sm" @blur="clearValidity('content')"></textarea>
                    </div>
                    <transition name="fade" mode="out-in">
                        <p v-if="!content.isValid" class="h-4 text-xs text-red-600">
                            {{ content.msg }}
                        </p>
                    </transition>
                </div>
            </div>
            <div class="mt-8 flex flex-col border border-black p-4">
                <h1>Dear {{ recipientName.val }},</h1>
                <p class="mt-2">{{ content.val }}</p>
                <div v-if="hasStudents" class="flex flex-col mt-4">
                    <h1 class="text-lg font-bold"><span class="underline">List of Students</span></h1>
                    <div class="flex flex-col mt-2">
                        <div class="flex flex-col overflow-x-auto">
                            <div class="inline-block min-w-full py-2">
                                <div class="overflow-x-auto">
                                    <table class="min-w-full text-left text-sm font-light">
                                        <thead class="border-b font-medium dark:border-neutral-500">
                                            <tr>
                                                <th scope="col" class="px-6 py-2 text-gray-400 w-8">#</th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">Fullname</th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">Student ID</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(item, index) in studentsList" :key="index" class="border-b border-dashed dark:border-neutral-500">
                                                <td class="whitespace-nowrap px-6 py-2 font-medium">{{ index + 1 }}</td>
                                                <td class="whitespace-nowrap px-6 py-2 capitalize hover:text-blue-600 hover:underline cursor-pointer" @click.stop="$emit('view-student-profile', item.student_id)">{{ item.student.name_en }}</td>
                                                <td class="whitespace-nowrap px-6 py-2">{{ item.student_id }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="flex justify-center mt-8">
                <div @click="draftEmail" class="flex justify-center items-center bg-ternary hover:bg-white border-ternary border px-4 py-2 rounded-lg cursor-pointer text-white hover:text-ternary">Draft Email</div>
            </div>
        </div>
        <div v-else class="w-full flex flex-col">
            <div v-if="viewType == 'confirmation'" class="mb-6">
                <h1 class="text-red-600">Please review the student information and training provider details carefully. Once you have confirmed, click the "Confirm" button to proceed with the transfer to <span class="font-semibold">{{ item.partner.en_company_name }}</span> (Training Provider).</h1>
            </div>
            <h1 class="text-xl font-bold underline">Group Details</h1>
            <div class="flex flex-row flex-wrap mt-4">
                <div class="w-1/4 font-bold">Reference Number</div>
                <div class="w-3/4">{{ item.ref_no }}</div>
            </div>
            <div class="flex flex-row flex-wrap mt-2">
                <div class="w-1/4 font-bold">Name of Group</div>
                <div class="w-3/4">{{ item.group_name }}</div>
            </div>
            <div class="flex flex-row flex-wrap mt-2">
                <div class="w-1/4 font-bold">Training Entity</div>
                <div class="w-3/4">{{ item.partner.en_company_name }}</div>
            </div>
            <div class="flex flex-row flex-wrap mt-2">
                <div class="w-1/4 font-bold">Specialization</div>
                <div class="w-3/4">{{ item.field_of_training }}</div>
            </div>
            <div class="flex flex-row flex-wrap mt-2">
                <div class="w-1/4 font-bold">Sector</div>
                <div class="w-3/4">{{ item.sector }}</div>
            </div>
            <div class="flex flex-row flex-wrap mt-2">
                <div class="w-1/4 font-bold">Location</div>
                <div class="w-3/4">{{ item.location }}</div>
            </div>
            <div class="flex flex-row flex-wrap mt-2">
                <div class="w-1/4 font-bold">Supervisor</div>
                <div class="w-3/4">{{ item.supervisor }}</div>
            </div>
            <div class="flex flex-row flex-wrap mt-2">
                <div class="w-1/4 font-bold">Start Date</div>
                <div class="w-3/4">{{ getInterviewDate(item.start_date) }}</div>
            </div>
            <div class="flex flex-row flex-wrap mt-2">
                <div class="w-1/4 font-bold">End Date</div>
                <div class="w-3/4">{{ getInterviewDate(item.end_date) }}</div>
            </div>
            <div class="flex flex-row flex-wrap mt-2">
                <div class="w-1/4 font-bold">Campus</div>
                <div class="w-3/4">{{ item.campus_code }}</div>
            </div>
            <div class="flex flex-row flex-wrap mt-2">
                <div class="w-1/4 font-bold">Program</div>
                <div class="w-3/4">{{ item.program_code }}</div>
            </div>
            <div v-if="item.training_document_url" class="flex flex-row flex-wrap mt-2">
                <div class="w-1/4 font-bold">Training Document</div>
                <div class="w-3/4">
                    <a target="_blank" class="text-blue-600 hover:underline" :href="getTraimningDocument">VIEW</a>
                </div>
            </div>
            <div v-if="hasStudents" class="flex flex-col mt-6">
                <h1 class="text-xl font-bold"><span class="underline">Added Students</span> ({{ selectedStudentsCount }})</h1>
                <div class="flex flex-col mt-2">
                    <div class="flex flex-col overflow-x-auto">
                        <div class="inline-block min-w-full py-2">
                            <div class="overflow-x-auto">
                                <table class="min-w-full text-left text-sm font-light">
                                    <thead class="border-b font-medium dark:border-neutral-500">
                                        <tr>
                                            <th scope="col" class="px-6 py-2 text-gray-400 w-8">#</th>
                                            <th scope="col" class="px-6 py-2 text-gray-400">Fullname</th>
                                            <th scope="col" class="px-6 py-2 text-gray-400">Student ID</th>
                                            <th scope="col" class="px-6 py-2 text-gray-400">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(item, index) in studentsList" :key="index" class="border-b border-dashed dark:border-neutral-500">
                                            <td class="whitespace-nowrap px-6 py-2 font-medium">{{ index + 1 }}</td>
                                            <td class="whitespace-nowrap px-6 py-2 capitalize hover:text-blue-600 hover:underline cursor-pointer" @click.stop="$emit('view-student-profile', item.student_id)">{{ item.student.name_en }}</td>
                                            <td class="whitespace-nowrap px-6 py-2">{{ item.student_id }}</td>
                                            <td class="whitespace-nowrap px-6 py-2">
                                                <div @click.stop="$emit('view-student-profile', item.student_id)" class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2">
                                                    <icon-eye class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-eye>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- <div v-if="hasUnSelectedStudents" class="flex flex-col mt-6">
                <h1 class="text-xl font-bold"><span class="underline">Removed Students</span> ({{ unSelectedStudentsCount }})</h1>
                <div class="flex flex-col mt-2">
                    <div class="flex flex-col overflow-x-auto">
                        <div class="inline-block min-w-full py-2">
                            <div class="overflow-x-auto">
                                <table class="min-w-full text-left text-sm font-light">
                                    <thead class="border-b font-medium dark:border-neutral-500">
                                        <tr>
                                            <th scope="col" class="px-6 py-2 text-gray-400 w-8">#</th>
                                            <th scope="col" class="px-6 py-2 text-gray-400">Fullname</th>
                                            <th scope="col" class="px-6 py-2 text-gray-400">Student ID</th>
                                            <th scope="col" class="px-6 py-2 text-gray-400">Reason</th>
                                            <th scope="col" class="px-6 py-2 text-gray-400">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(item, index) in unSelectedstudentsList" :key="index" class="border-b border-dashed dark:border-neutral-500">
                                            <td class="whitespace-nowrap px-6 py-2 font-medium">{{ index + 1 }}</td>
                                            <td class="whitespace-nowrap px-6 py-2 capitalize hover:text-blue-600 hover:underline cursor-pointer" @click.stop="$emit('view-student-profile', item.student_id)">{{ item.student.name_en }}</td>
                                            <td class="whitespace-nowrap px-6 py-2">{{ item.student_id }}</td>
                                            <td class="whitespace-nowrap px-6 py-2">{{ item.reason }}</td>
                                            <td class="whitespace-nowrap px-6 py-2">
                                                <div @click.stop="$emit('view-student-profile', item.student_id)" class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2">
                                                    <icon-eye class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110 hover:scale-110"></icon-eye>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div> -->
            <div v-if="hasApprovals && isNotPartner" class="flex flex-col mt-6">
                <h1 class="text-xl font-bold underline">Workflow Actions</h1>
                <div class="flex flex-col mt-2">
                    <div class="flex flex-col overflow-x-auto">
                        <div class="inline-block min-w-full py-2">
                            <div class="overflow-x-auto">
                                <table class="min-w-full text-left text-sm font-light">
                                    <thead class="border-b font-medium dark:border-neutral-500">
                                        <tr>
                                            <th scope="col" class="px-6 py-2 text-gray-400 w-8">#</th>
                                            <th scope="col" class="px-6 py-2 text-gray-400">Fullname</th>
                                            <th scope="col" class="px-6 py-2 text-gray-400">Role</th>
                                            <th scope="col" class="px-6 py-2 text-gray-400">Note</th>
                                            <th scope="col" class="px-6 py-2 text-gray-400">Action</th>
                                            <th scope="col" class="px-6 py-2 text-gray-400">Action At</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(item, index) in approvalList" :key="index" class="border-b border-dashed dark:border-neutral-500">
                                            <td class="whitespace-nowrap px-6 py-2 font-medium">{{ index + 1 }}</td>
                                            <td class="whitespace-nowrap px-6 py-2 capitalize">{{ item.user.name }}</td>
                                            <td class="whitespace-nowrap px-6 py-2">{{ item.role }}</td>
                                            <td class="whitespace-nowrap px-6 py-2">{{ item.note }}</td>
                                            <td class="whitespace-nowrap px-6 py-2">{{ getStatus(item.approved) }}</td>
                                            <td class="whitespace-nowrap px-6 py-2">{{ getInterviewDateTime(item.approved_at) }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="viewType == 'confirmation'" class="flex justify-center mt-6">
                <div @click="confirmApplication" class="flex justify-center items-center bg-ternary hover:bg-white border-ternary border px-4 py-2 rounded-lg cursor-pointer text-white hover:text-ternary">Confirm</div>
            </div>
        </div>
    </div>
</template>

<script>
import IconEye from "../../components/icons/IconEye.vue";
import { formattedDate, formattedDateWithTimeAmPm } from "../../helpers/utils";
export default {
    name: "internship-view",
    components: { IconEye, },
    emits: ["view-student-profile", "confirm-application"],
    props: ["item", "viewType", "collegeStudentPrograms"],
    data() {
        return {
            isDraftEmail: false,
            subject: {
                val: "Internship for ATS School",
                isValid: true,
                msg: "",
            },
            recipientName: {
                val: this.item.partner.en_company_name,
                isValid: true,
                msg: "",
            },
            content: {
                val: "I trust this email finds you well. The students mentioned below are interested in an internship at your company. Please kindly grant approval for the students.",
                isValid: true,
                msg: "",
            },
            formIsValid: true,
        };
    },
    computed: {
        role() {
            return this.$store.getters.role;
        },
        isNotPartner() {
            return this.role != 'partner';
        },
        selectedStudentsCount() {
            return this.item.internship_candidates.length;
        },
        unSelectedStudentsCount() {
            return this.item.internship_unselected_candidates.length;
        },
        hasStudents() {
            return this.selectedStudentsCount > 0;
        },
        hasUnSelectedStudents() {
            return this.unSelectedStudentsCount > 0;
        },
        hasApprovals() {
            return this.item.internship_approvals.length > 0;
        },
        studentsList() {
            return this.item.internship_candidates;
        },
        unSelectedstudentsList() {
            return this.item.internship_unselected_candidates;
        },
        approvalList() {
            return this.item.internship_approvals.filter(item => item.approved !== null && item.approved !== 3);
        },
        getTraimningDocument() {
            return process.env.VUE_APP_PUBLIC_URL + this.item.training_document_url;
        },
    },
    methods: {
        getInterviewDate(date) {
            return formattedDate(date);
        },
        getInterviewDateTime(date) {
            return formattedDateWithTimeAmPm(date);
        },
        getStatus(status) {
            switch (status) {
                case 0:
                    return 'Rejected';
                case 1:
                    return 'Approved';
                case 2:
                    return 'Returned';
                case 3:
                    return 'Expired';
                case 4:
                    return 'Created';
                case 5:
                    return 'Updated';
                default:
                    return '';
            }
        },
        confirmApplication() {
            if (this.viewType == 'confirmation') {
                this.isDraftEmail = true;
            } else {
                this.$emit('confirm-application');
            }
        },
        clearValidity(input) {
            this[input].isValid = true;
            this[input].msg = "";
        },
        validateForm() {
            this.formIsValid = true;
            
            if (this.subject.val === "") {
                this.subject.isValid = false;
                this.subject.msg = "Subject must not be empty.";
                this.formIsValid = false;
            }
            if (this.recipientName.val === "") {
                this.recipientName.isValid = false;
                this.recipientName.msg = "Recipient's Name must not be empty.";
                this.formIsValid = false;
            }
            if (this.content.val === "") {
                this.content.isValid = false;
                this.content.msg = "Content must not be empty.";
                this.formIsValid = false;
            }
        },
        draftEmail() {
            this.validateForm();
            
            if (!this.formIsValid) {
                return;
            }

            const formData = {
                subject: this.subject.val,
                recipient_name: this.recipientName.val,
                content: this.content.val,
            }

            this.$emit('confirm-application', formData);
        },
    },
}
</script>

<style lang="scss" scoped>
.form-input {
    input[type="text"],
    input[type="email"],
    input[type="number"],
    input[type="password"],
    input[type="date"],
    input[type="datetime-local"],
    select {
        @apply block w-full border rounded-lg py-2 px-2;
    }
    input[type="text"]:focus,
    input[type="email"]:focus,
    input[type="number"]:focus,
    input[type="password"]:focus,
    input[type="date"]:focus,
    input[type="datetime-local"]:focus,
    select:focus {
        outline: none;
        border-color: #394867;
        box-shadow: 0 0 0 2px #394867;
    }
}
.invalid label {
  @apply text-red-600;
}
.invalid input,
.invalid select {
  border: 1px solid red;
}
input[type="checkbox"] {
  @apply w-auto;
}
.fade-enter-active,
.fade-leave-active {
  transition: all 0.5s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  height: 0px;
}
.invalid_ {
    border: 1px solid red;
}
</style>